

// ---------------------------------------------------------------------------
// Client <-> server clock-offset estimator.
//
// The graphs draw their live right edge at "now". If we use the local device
// clock (Date.now()) and that device is out of sync with the server, the open
// bar extrapolates past the newest real sample and then snaps backward when the
// next sample lands stamped on server time. Fixing the server clock cured the
// desktop, but every client has its own clock we don't control (phones,
// tablets). This module estimates each client's offset from the server so the
// renderer can draw a *server-aligned* now that doesn't depend on the local
// clock being right.
//
// How the estimate is formed:
//   rawOffset = clientNow - serverTimestamp  (captured at message ARRIVAL)
// This is (true skew + transit latency). Latency is one-sided: it's always >= 0
// and spikes upward on a slow packet, never downward. So the cleanest estimate
// of true skew is the MINIMUM rawOffset over a sliding window, not the mean --
// a mean bakes in average latency and wobbles on every slow message. The
// fastest packet in the window is the least latency-contaminated, so its offset
// is closest to pure skew. Aging samples out of the window lets the estimate
// track the client's clock genuinely drifting over time.
//
// Applying the estimate:
//   The renderer must not jump when the target offset changes, or the
//   correction itself becomes a visible snap. So getServerNow() slews the
//   applied offset toward the target instead of stepping to it -- and eases
//   *forward* (edge moving right) faster than *backward*, since a backward
//   correction is exactly the snap we're hiding. A first lock or a large
//   resync (e.g. the OS correcting the phone mid-session) snaps once.
// ---------------------------------------------------------------------------

const WINDOW_MS         = 3 * 60 * 1000; // keep ~3 min of arrival samples
const STEP_THRESHOLD_MS = 1500;          // jump to target on first lock / big resync
const SLEW_GAIN_FWD     = 0.12;          // ease toward target when edge moves right (forward)
const SLEW_GAIN_BACK    = 0.05;          // ease slower when edge moves left (the snap direction)

let samples       = [];   // [{ at: clientNowMs, raw: clientNow - serverNow }]
let targetOffset  = 0;    // min raw over window  (~= skew + floor latency)
let appliedOffset = 0;    // slewed value the renderer actually uses
let primed        = false;

// Same unit convention as scripts/support.js timeToMillis: numbers are already
// ms epoch; anything else is parsed via Date.
function toMillis(t) {
    return typeof t === 'number' ? t : new Date(t).getTime();
}

// Call on every data arrival, with the packet's server timestamp. Pairs the
// receipt instant (Date.now()) with that server time and refreshes the target.
export function recordServerTimestamp(serverTimestamp) {
    const server = toMillis(serverTimestamp);
    if (!Number.isFinite(server)) return;

    const now = Date.now();
    samples.push({ at: now, raw: now - server });

    const cutoff = now - WINDOW_MS;
    if (samples[0] && samples[0].at < cutoff) {
        samples = samples.filter(s => s.at >= cutoff);
    }

    let min = Infinity;
    for (const s of samples) if (s.raw < min) min = s.raw;
    targetOffset = min;

    if (!primed) {            // first ever sample: adopt immediately, no slew
        appliedOffset = targetOffset;
        primed = true;
    }
}

// Call once per render frame. Eases the applied offset toward target and
// returns the server-aligned "now" to use as the graph's right edge.
export function getServerNow() {
    if (!primed) return Date.now();   // no data yet -> fall back to local clock

    const diff = targetOffset - appliedOffset;
    if (Math.abs(diff) > STEP_THRESHOLD_MS) {
        appliedOffset = targetOffset;                       // big resync: snap once
    } else {
        // diff < 0 -> appliedOffset shrinks -> serverNow grows -> edge moves
        // RIGHT (forward, ease faster).  diff > 0 -> edge moves LEFT (backward,
        // the snap direction, ease slower).
        const gain = diff < 0 ? SLEW_GAIN_FWD : SLEW_GAIN_BACK;
        appliedOffset += diff * gain;
    }
    return Date.now() - appliedOffset;
}

// Handy for debugging / an on-screen readout if you want one.
export function getClockOffset() {
    return { targetOffset, appliedOffset, sampleCount: samples.length, primed };
}